import { useCallback, useRef } from 'react';

export type UseFileInputOptions = { accept?: string; multiple?: boolean } & (
  | { onFileSelected: (file: File) => void | Promise<void> }
  /** Every picked file at once (with `multiple`, there can be several). */
  | { onFilesSelected: (files: File[]) => void }
);

/**
 * Hook for triggering file input dialog and handling file selection.
 * Creates a hidden file input, triggers click, and handles file selection.
 *
 * @example
 * const triggerFileInput = useFileInput({
 *   accept: 'image/*',
 *   onFileSelected: async (file) => {
 *     await uploadFile(file);
 *   }
 * });
 *
 * <button onClick={triggerFileInput}>Upload Image</button>
 */
export function useFileInput(options: UseFileInputOptions): () => void {
  const latest = useRef(options);
  latest.current = options;
  const { accept, multiple = false } = options;
  return useCallback(() => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      if (accept) {
        input.accept = accept;
      }
      input.multiple = multiple;
      input.style.display = 'none';
      document.body.appendChild(input);

      input.onchange = async e => {
        const files = Array.from((e.target as HTMLInputElement).files ?? []);
        const current = latest.current;
        try {
          if ('onFilesSelected' in current) {
            if (files.length > 0) current.onFilesSelected(files);
          } else if (files[0]) {
            await current.onFileSelected(files[0]);
          }
        } catch (error) {
          console.error('File selection error:', error);
        }
        // Clean up
        document.body.removeChild(input);
      };

      // Handle cancellation
      input.oncancel = () => {
        document.body.removeChild(input);
      };

      input.click();
    } catch (error) {
      console.error('Error creating file input:', error);
    }
  }, [accept, multiple]);
}
